import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { loadYouTubeAPI } from '../utils/youtubeLoader';

const VideoSection = ({ onVideoClick, isModalOpen = false }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [userInteracted, setUserInteracted] = useState(false);
    const iframeRef = useRef(null);
    const playerRef = useRef(null);
    const { ref, inView } = useInView({
        threshold: 0.5,
        triggerOnce: false
    });

    useEffect(() => {
        // Load YouTube Player API with shared loader
        loadYouTubeAPI(() => {
            // Add small delay to prevent simultaneous initialization
            setTimeout(() => {
                if (iframeRef.current && !playerRef.current) {
                    try {
                        playerRef.current = new window.YT.Player(iframeRef.current, {
                            videoId: 'JvNI-deDepw',
                            playerVars: {
                                autoplay: 0, // Never autoplay - require user interaction
                                loop: 1,
                                playlist: 'JvNI-deDepw',
                                mute: 1,
                                controls: 1,
                                showinfo: 0,
                                rel: 0,
                                modestbranding: 1,
                                iv_load_policy: 3,
                                fs: 1,
                                disablekb: 0,
                                start: 0,
                                enablejsapi: 1,
                                playsinline: 1,
                                cc_load_policy: 0,
                                widget_referrer: window.location.origin
                            },
                            events: {
                                onReady: (event) => {
                                    // Reduced logging to avoid triggering bot detection
                                    setIsReady(true);
                                },
                                onStateChange: (event) => {
                                    if (event.data === window.YT.PlayerState.PLAYING) {
                                        setIsPlaying(true);
                                    } else if (event.data === window.YT.PlayerState.PAUSED) {
                                        setIsPlaying(false);
                                    }
                                },
                                onError: (event) => {
                                    // Only log actual playback errors, not analytics errors
                                    if (event.data !== 150 && event.data !== 101) {
                                        console.error('VideoSection: YouTube player error', event.data);
                                    }
                                }
                            }
                        });
                    } catch (error) {
                        console.error('VideoSection: Error creating YouTube player', error);
                    }
                }
            }, 100); // Small delay to stagger initialization
        });
    }, []);

    useEffect(() => {
        if (playerRef.current && isReady) {
            let playTimer = null;
            
            // Autoplay when video comes into view (muted, as per browser policy)
            if (inView && !isPlaying) {
                playTimer = setTimeout(() => {
                    if (playerRef.current && inView && !isPlaying) {
                        try {
                            // Ensure video is muted before autoplay (browser requirement)
                            playerRef.current.mute();
                            setIsMuted(true);
                            playerRef.current.playVideo();
                            setIsPlaying(true);
                        } catch (error) {
                            // Silently handle errors
                        }
                    }
                }, 300);
            } else if (!inView && isPlaying) {
                // Pause when video goes out of view
                try {
                    playerRef.current.pauseVideo();
                    setIsPlaying(false);
                } catch (error) {
                    // Silently handle errors
                }
            }
            
            return () => {
                if (playTimer) clearTimeout(playTimer);
            };
        }
    }, [inView, isPlaying, isReady]);

    // Mute video when modal is open
    useEffect(() => {
        if (playerRef.current && isModalOpen) {
            playerRef.current.mute();
            setIsMuted(true);
        }
    }, [isModalOpen]);

    const togglePlayPause = () => {
        // Mark that user has interacted (for unmuting)
        setUserInteracted(true);
        if (playerRef.current) {
            if (isPlaying) {
                playerRef.current.pauseVideo();
                setIsPlaying(false);
            } else {
                playerRef.current.playVideo();
                setIsPlaying(true);
                // Unmute after user interaction (browser allows unmuting after user interaction)
                if (isMuted) {
                    setTimeout(() => {
                        if (playerRef.current) {
                            playerRef.current.unMute();
                            setIsMuted(false);
                        }
                    }, 300);
                }
            }
        }
    };

    const toggleMute = () => {
        if (playerRef.current) {
            if (isMuted) {
                playerRef.current.unMute();
                setIsMuted(false);
            } else {
                playerRef.current.mute();
                setIsMuted(true);
            }
        }
    };

    const handleVideoAreaClick = () => {
        // Mark that user has interacted (for unmuting)
        setUserInteracted(true);
        // Unmute video when user clicks on it
        if (playerRef.current && isMuted) {
            playerRef.current.unMute();
            setIsMuted(false);
        }
        if (onVideoClick) {
            onVideoClick('JvNI-deDepw');
        }
    };

    return (
        <section
            ref={ref}
            className="relative w-full h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900"
        >
            {/* Video Background - clickable to open modal */}
            <div
                className="absolute inset-0 z-0 cursor-pointer"
                onClick={handleVideoAreaClick}
            >
                <div className="relative w-full h-full overflow-hidden">
                    <div
                        ref={iframeRef}
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto"
                        style={{
                            width: '200vh',
                            height: '120vh',
                            minWidth: '120vw',
                            minHeight: '70vw'
                        }}
                    />
                </div>
                <div className="absolute inset-0 bg-black/10"></div>
            </div>

            {/* Play/Pause Button - Bottom Left */}
            <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                    e.stopPropagation();
                    togglePlayPause();
                }}
                className="absolute bottom-20 left-8 z-20 inline-flex items-center space-x-2 bg-black/50 backdrop-blur-sm hover:bg-black/70 text-white px-4 py-2 rounded-full transition-all duration-300"
            >
                {isPlaying ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                    </svg>
                ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v14l11-7z" />
                    </svg>
                )}
                <span className="text-sm font-medium">
                    {isPlaying ? 'Pause' : 'Play'}
                </span>
            </motion.button>

            {/* Mute/Unmute Button - Bottom Left */}
            <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                    e.stopPropagation();
                    toggleMute();
                }}
                className="absolute bottom-8 left-8 z-20 inline-flex items-center space-x-2 bg-black/50 backdrop-blur-sm hover:bg-black/70 text-white px-4 py-2 rounded-full transition-all duration-300"
            >
                {isMuted ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                )}
                <span className="text-sm font-medium">
                    {isMuted ? 'Unmute' : 'Mute'}
                </span>
            </motion.button>

            {/* Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                viewport={{ once: true }}
                className="absolute bottom-8 right-8"
            >
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
                >
                    <motion.div
                        animate={{ y: [0, 12, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-1 h-3 bg-white rounded-full mt-2"
                    />
                </motion.div>
            </motion.div>
        </section>
    );
};

export default VideoSection;
